/** Phase 5H / MER-09 — SCR-MER-059–062 staff & PIN bindings (07-02) */

export const STAFF_PIN_SCREEN_IDS = [
  'SCR-MER-059',
  'SCR-MER-060',
  'SCR-MER-061',
  'SCR-MER-062',
] as const;

export type StaffPinScreenId = (typeof STAFF_PIN_SCREEN_IDS)[number];

export const STAFF_PIN_ROUTES: Record<StaffPinScreenId, string> = {
  'SCR-MER-059': '/staff-list',
  'SCR-MER-060': '/invite-staff',
  'SCR-MER-061': '/staff-detail',
  'SCR-MER-062': '/pin-sign-in',
};

export const STAFF_ACTION_PERMISSIONS = {
  list: 'PERM-tenant.staff.list',
  invite: 'PERM-tenant.staff.invite',
  update: 'PERM-tenant.staff.update',
  remove: 'PERM-tenant.staff.remove',
  pinIssue: 'PERM-identity.pin.issue',
  mfaReset: 'PERM-identity.mfa.reset',
} as const;

export const STAFF_PIN_PERMISSIONS: Partial<Record<StaffPinScreenId, readonly string[]>> = {
  'SCR-MER-059': [STAFF_ACTION_PERMISSIONS.list],
  'SCR-MER-060': [STAFF_ACTION_PERMISSIONS.invite],
  'SCR-MER-061': [
    STAFF_ACTION_PERMISSIONS.update,
    STAFF_ACTION_PERMISSIONS.remove,
    STAFF_ACTION_PERMISSIONS.pinIssue,
    STAFF_ACTION_PERMISSIONS.mfaReset,
    STAFF_ACTION_PERMISSIONS.invite,
  ],
};

export const STAFF_PIN_ENDPOINTS: Partial<Record<StaffPinScreenId, readonly string[]>> = {
  'SCR-MER-059': ['GET /merchant/v1/staff', 'GET /merchant/v1/branches'],
  'SCR-MER-060': [
    'POST /merchant/v1/staff/invitations',
    'GET /merchant/v1/branches',
    'GET /merchant/v1/staff',
    'POST /merchant/v1/staff/invitations/{id}/cancel',
    'POST /merchant/v1/staff/invitations/{id}/resend',
  ],
  'SCR-MER-061': [
    'GET /merchant/v1/staff',
    'PATCH /merchant/v1/staff/{id}',
    'POST /merchant/v1/staff/{id}/pin',
    'POST /merchant/v1/staff/{id}/mfa/reset',
    'POST /merchant/v1/staff/invitations/{id}/cancel',
    'POST /merchant/v1/staff/invitations/{id}/resend',
    'DELETE /merchant/v1/staff/{id}',
  ],
  /** 10-03 device token lists PIN-eligible staff at this branch. */
  'SCR-MER-062': ['GET /merchant/v1/auth/staff', 'POST /merchant/v1/auth/staff/pin'],
};

export function staffPermissionLabel(code: string): string {
  return code.replace(/^PERM-/, '');
}

export const STAFF_ID_STORAGE_KEY = 'beatq.merchant.staffId';

/** Live MOD-004 invite/PATCH `roleCode` enum (not inventory abbreviations). */
export const STAFF_API_ROLE_OPTIONS = [
  { code: 'BUSINESS_OWNER', label: 'Business owner', short: 'BO' },
  { code: 'BUSINESS_MANAGER', label: 'Business manager', short: 'BM' },
  { code: 'FINANCE_MANAGER', label: 'Finance manager', short: 'FIN' },
  { code: 'BRANCH_MANAGER', label: 'Branch manager', short: 'BM-BR' },
  { code: 'SHIFT_SUPERVISOR', label: 'Shift supervisor', short: 'BS' },
  { code: 'KITCHEN_STAFF', label: 'Kitchen staff', short: 'KIT' },
  { code: 'PICKER', label: 'Picker', short: 'PCK' },
  { code: 'FRONT_OF_HOUSE', label: 'Front of house / cashier', short: 'CSH' },
  { code: 'READ_ONLY', label: 'Read only', short: 'RO' },
] as const;

export const STAFF_ROLE_CODES = STAFF_API_ROLE_OPTIONS.map((o) => o.code);

export const BRANCH_SCOPED_STAFF_ROLES = new Set<string>([
  'BRANCH_MANAGER',
  'SHIFT_SUPERVISOR',
  'KITCHEN_STAFF',
  'PICKER',
  'FRONT_OF_HOUSE',
]);

const MER_TO_API: Record<string, string> = {
  'ROLE-MER-001': 'BUSINESS_OWNER',
  'ROLE-MER-002': 'BUSINESS_MANAGER',
  'ROLE-MER-003': 'SHIFT_SUPERVISOR',
  'ROLE-MER-004': 'FRONT_OF_HOUSE',
  'ROLE-MER-005': 'KITCHEN_STAFF',
  'ROLE-MER-006': 'PICKER',
  'ROLE-MER-007': 'FINANCE_MANAGER',
  'ROLE-MER-008': 'PICKER',
};

/** Map stored ROLE-MER-* / API enum → invite/PATCH roleCode. */
export function apiRoleFromStored(roleCode: string | undefined | null): string {
  if (!roleCode) return 'SHIFT_SUPERVISOR';
  if ((STAFF_ROLE_CODES as readonly string[]).includes(roleCode)) return roleCode;
  return MER_TO_API[roleCode] ?? roleCode;
}

export function roleRequiresBranches(roleCode: string): boolean {
  return BRANCH_SCOPED_STAFF_ROLES.has(roleCode);
}

export function staffRoleLabel(roleCode: string | undefined | null): string {
  if (!roleCode) return '—';
  const api = apiRoleFromStored(roleCode);
  const opt = STAFF_API_ROLE_OPTIONS.find((o) => o.code === api);
  if (opt) return `${opt.label} (${opt.short})`;
  return roleCode;
}
